/**
 * src/components/FAQ.js
 *
 * Accordion FAQ component with search filter.
 * Receives FAQ items from helpData.js or as a prop.
 */

import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Search } from "lucide-react";
import { getFaqItems } from "../data/helpData";
import { errorDev } from '../utils/logger.js';

export default function FAQ({ items: propItems }) {
  const { t, i18n, ready: i18nReady } = useTranslation("support");
  const lang = i18n.language?.split("-")[0] || "en";

  const allItems = propItems || getFaqItems(lang);

  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);

  const safeT = (key, fallback) => {
    if (!i18nReady) return fallback;
    try {
      const result = t(key);
      return result === key ? fallback : result;
    } catch (err) {
      errorDev("Operation failed:", err);
      return fallback;
    }
  };

  const filtered = useMemo(() => {
    if (!query.trim()) return allItems;
    const q = query.trim().toLowerCase();
    return allItems.filter(
      (item) =>
        item.question.toLowerCase().includes(q) ||
        item.answer.toLowerCase().includes(q)
    );
  }, [allItems, query]);

  return (
    <div>
      {/* Search */}
      <div className="relative mb-4">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={safeT("faq_search_placeholder", "Search questions...")}
          className="ui-input pl-9"
          aria-label="Search FAQ"
        />
        <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
        {query && (
          <button
            onClick={() => setQuery("")}
            className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {/* Items */}
      {filtered.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
          {safeT("no_results", "No results found.")}
        </p>
      ) : (
        <div className="space-y-2">
          {filtered.map((item) => {
            const isOpen = openId === item.id;
            return (
              <div
                key={item.id}
                className="ui-card rounded-xl overflow-hidden transition-shadow hover:shadow-sm"
              >
                <button
                  onClick={() => setOpenId(isOpen ? null : item.id)}
                  aria-expanded={isOpen}
                  className="w-full text-left px-4 py-3.5 flex items-center justify-between gap-3 hover:bg-gray-50 dark:hover:bg-neutral-700/50 transition"
                >
                  <span className="text-sm font-medium text-gray-900 dark:text-white pr-2">
                    {item.question}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-transform duration-200 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 text-sm text-gray-600 dark:text-neutral-300 leading-relaxed border-t border-gray-100 dark:border-neutral-700/60 pt-3">
                    {item.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
